import React, { useEffect } from "react";
import { ArrowLeft, ArrowRight, ChevronRight, Plus } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FlowLines } from "@/components/site/FlowLines";
import { Container, Label, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import lexikon from "@/data/lexikon.json";

const sorted = Object.entries(lexikon).sort((a, b) => a[1].term.localeCompare(b[1].term, "de"));

export default function LexikonTerm({ slug }) {
  const d = lexikon[slug];
  const idx = sorted.findIndex(([s]) => s === slug);
  const prev = idx > 0 ? sorted[idx - 1] : null;
  const next = idx < sorted.length - 1 ? sorted[idx + 1] : null;
  const isPersonal = d.kategorie === "Personal & Berufe";

  useEffect(() => {
    document.title = d.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", d.metaDescription);
    window.scrollTo(0, 0);
  }, [d]);

  return (
    <>
      <Header />
      <main data-testid={`lexikon-term-${slug}`}>
        <section className="has-lines relative overflow-hidden bg-[#1e2532] pb-16 pt-[150px] text-white on-dark">
          <FlowLines />
          <Container className="max-w-[820px]">
            <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
              <a href="/" className="hover:text-white">Startseite</a>
              <ChevronRight size={13} />
              <a href="/lexikon/" className="hover:text-white">Lexikon</a>
              <ChevronRight size={13} />
              <span className="text-white/80">{d.term}</span>
            </nav>
            <Label>{d.kategorie}</Label>
            <h1 className="mt-7 font-bold" data-testid="lexikon-term-h1">
              {d.term}
            </h1>
          </Container>
        </section>

        <article className="section-pad bg-white">
          <Container className="max-w-[820px]">
            <div
              data-testid="lexikon-definition"
              className="rounded-[16px] border border-[#0f9d6e]/25 bg-[#f2f8f5] p-7 md:p-9"
            >
              <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]">Definition</p>
              <p className="mt-4 text-[17.5px] leading-[1.8] text-[#1e2532]">{d.definition}</p>
            </div>

            <h2 className="mt-14 text-[24px] font-bold leading-[1.3] text-[#1e2532] md:text-[27px]">
              Bedeutung in der Praxis
            </h2>
            {d.praxis.map((p) => (
              <p key={p.slice(0, 40)} className="mt-5 text-[16.5px] leading-[1.85] text-[#3c4356]">
                {p}
              </p>
            ))}

            <h2 className="mt-14 text-[24px] font-bold leading-[1.3] text-[#1e2532] md:text-[27px]">Häufige Fragen</h2>
            <div className="mt-6 border-t border-[#dfe2e0]">
              {d.faq.map((f, i) => (
                <div key={f.q} className="border-b border-[#dfe2e0] py-6" data-testid={`lexikon-faq-${i}`}>
                  <h3 className="flex items-start gap-3 text-[17px] font-semibold leading-[1.45] text-[#1e2532]">
                    <Plus size={18} className="mt-[3px] shrink-0 text-[#0f9d6e]" />
                    {f.q}
                  </h3>
                  <p className="mt-3 pl-[30px] text-[16px] leading-[1.8] text-[#565f70]">{f.a}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-14 text-[24px] font-bold leading-[1.3] text-[#1e2532] md:text-[27px]">
              Verwandte Begriffe
            </h2>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {d.verwandt
                .filter((s) => lexikon[s])
                .map((s) => (
                  <a
                    key={s}
                    href={`/lexikon/${s}/`}
                    data-testid={`lexikon-verwandt-${s}`}
                    className="rounded-full border border-[#d4d8d5] bg-[#f8f9f8] px-4 py-2 text-[14px] font-medium text-[#3c4356] hover:border-[#0f9d6e] hover:text-[#0b7d57]"
                    style={{ transition: "border-color .3s ease, color .3s ease" }}
                  >
                    {lexikon[s].term}
                  </a>
                ))}
            </div>

            <aside data-testid="lexikon-cta" className="mt-14 rounded-[18px] bg-[#1e2532] p-8 text-white md:p-10">
              <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#3ecf9b]">
                {isPersonal ? "Personal" : "Projektentwicklung"}
              </p>
              <p className="mt-4 font-display text-[21px] font-medium leading-[1.45] md:text-[24px]">
                {isPersonal
                  ? "Sie möchten genau diese Rolle besetzen? Wir melden uns innerhalb von 24 Stunden mit einer Einschätzung."
                  : "Sie entwickeln Projekte und brauchen Flächen, Netzanfragen oder Investoren? Lassen Sie uns 10 Minuten sprechen."}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-6">
                <a
                  href={isPersonal ? "/personalvermittlung/#formular" : "/#kontakt"}
                  className="btn-solid inline-flex items-center px-7 py-[15px] text-[13px] font-bold uppercase tracking-[0.06em]"
                >
                  <span>{isPersonal ? "Stelle besetzen" : "Erstgespräch vereinbaren"}</span>
                </a>
                {d.links.map((l) => (
                  <a key={l.href} href={l.href} className="inline-flex items-center gap-2 text-[14.5px] font-semibold text-[#3ecf9b]">
                    <span className="link-underline">{l.label}</span>
                    <ArrowRight size={15} />
                  </a>
                ))}
              </div>
            </aside>

            <nav className="mt-12 flex flex-wrap justify-between gap-6 border-t border-[#dfe2e0] pt-8" aria-label="Weitere Begriffe">
              {prev ? (
                <a href={`/lexikon/${prev[0]}/`} data-testid="lexikon-prev" className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#1e2532]">
                  <ArrowLeft size={16} className="text-[#0f9d6e]" />
                  <span className="link-underline">{prev[1].term}</span>
                </a>
              ) : (
                <span />
              )}
              {next && (
                <a href={`/lexikon/${next[0]}/`} data-testid="lexikon-next" className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#1e2532]">
                  <span className="link-underline">{next[1].term}</span>
                  <ArrowRight size={16} className="text-[#0f9d6e]" />
                </a>
              )}
            </nav>
          </Container>
        </article>
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
